import { useEffect, useRef, useState } from 'react';
import { X, Loader2, CheckCircle2, XCircle, ImagePlus, Download, Image as ImageIcon } from 'lucide-react';
import { generatePoster } from '../api/posters';

export default function PosterModal({ open, onClose, nominee }) {
  const [step, setStep]         = useState('upload'); // upload | generating | done
  const [flashErr, setFlashErr] = useState('');
  const [image, setImage]       = useState(null);
  const [preview, setPreview]   = useState(null);
  const [posterUrl, setPosterUrl] = useState(null);
  const fileRef                 = useRef(null);

  useEffect(() => {
    if (open) {
      setStep('upload');
      setFlashErr('');
      setImage(null);
      setPreview(null);
      setPosterUrl(null);
    }
    return () => {
      document.body.classList.remove('modal-open-scroll-lock');
    };
  }, [open]);

  useEffect(() => {
    if (open) document.body.classList.add('modal-open-scroll-lock');
    else       document.body.classList.remove('modal-open-scroll-lock');
  }, [open]);

  if (!open) return null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
    setFlashErr('');
  };

  const handleGenerate = async () => {
    if (!image) { setFlashErr('Please select a photo.'); return; }
    setFlashErr('');
    setStep('generating');
    try {
      const blob = await generatePoster(nominee.votingCode, image);
      setPosterUrl(URL.createObjectURL(blob));
      setStep('done');
    } catch (e) {
      setFlashErr(e?.response?.data?.message || e?.message || 'Could not generate poster.');
      setStep('upload');
    }
  };

  const isBusy = step === 'generating';

  return (
    <div
      className="payment-modal-backdrop"
      onClick={(e) => !isBusy && e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className="payment-modal card border-0 shadow-lg">

        <div className="ink-surface p-4 position-relative">
          {!isBusy && (
            <button type="button" onClick={onClose} className="btn btn-sm position-absolute top-0 end-0 m-2 text-white opacity-75" aria-label="Close">
              <X size={18} />
            </button>
          )}
          <div className="d-flex align-items-center gap-3">
            <div className="rounded-circle gold-chip d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 42, height: 42 }}>
              <ImageIcon size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 className="font-display h5 mb-0 text-white">Generate Your Poster</h2>
              <p className="mb-0 small text-white-50 text-truncate">
                {nominee?.name} &bull; {nominee?.categoryName}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4">

          {flashErr && (
            <div className="alert alert-danger d-flex align-items-start gap-2 py-2 px-3 mb-3 small">
              <XCircle size={15} className="flex-shrink-0 mt-1" />
              <span>{flashErr}</span>
            </div>
          )}

          {step === 'upload' && (
            <>
              <div className="nominee-notice mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-1">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>Select a good quality photo for your poster. JPG or PNG only. Max size: 2MB.</span>
              </div>
              <div
                className="nominee-photo-dropzone mb-3"
                onClick={() => fileRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="nominee-photo-preview" />
                ) : (
                  <div className="d-flex flex-column align-items-center gap-2 text-muted">
                    <ImagePlus size={28} />
                    <span className="small">Click to select photo</span>
                  </div>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                className="d-none"
                onChange={handleFileChange}
              />
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary flex-grow-1" onClick={onClose}>Cancel</button>
                <button className="btn gold-chip flex-grow-1 fw-semibold" disabled={!image} onClick={handleGenerate}>Generate Poster</button>
              </div>
            </>
          )}

          {step === 'generating' && (
            <div className="text-center py-3">
              <Loader2 size={32} className="text-warning mb-2 spin" />
              <p className="text-muted small mb-0">Generating your poster…</p>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-2">
              <div className="mx-auto rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                   style={{ width: 56, height: 56, background: 'rgba(25,135,84,0.15)' }}>
                <CheckCircle2 size={32} className="text-success" />
              </div>
              <h3 className="font-display fs-5 mb-1">Poster ready!</h3>
              {posterUrl && (
                <img src={posterUrl} alt="Generated poster" className="img-fluid rounded mb-3" style={{ maxHeight: 300 }} />
              )}
              <a
                href={posterUrl}
                download={`${nominee?.name || 'poster'}.png`}
                className="btn gold-chip w-100 fw-semibold d-inline-flex align-items-center justify-content-center gap-2"
              >
                <Download size={16} /> Download Poster
              </a>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
